import React, {
  useCallback,
  useMemo,
  useState,
  Children,
  isValidElement,
  type ReactElement,
} from 'react'
import type { AccordionItemProps, AccordionProps } from './Accordion.types'
import { AccordionContext } from './AccordionContext'
import './Accordion.css'

type AccordionItemElement = ReactElement<AccordionItemProps>

const getItemId = (child: AccordionItemElement, index: number): string =>
  child.props.id ?? `accordion-item-${index}`

const Accordion: React.FC<AccordionProps> = ({
  grouped = false,
  allowMultiple = false,
  className = '',
  style,
  children,
  openIds,
  defaultOpenIds,
  onOpenChange,
  darkMode = false,
}) => {
  const childArray = Children.toArray(children).filter(
    (child): child is AccordionItemElement =>
      isValidElement<AccordionItemProps>(child),
  )

  const initialOpenIds = useMemo(
    () =>
      defaultOpenIds ??
      childArray
        .filter((child) => Boolean(child.props.defaultExpanded))
        .map((child, index) => getItemId(child, index)),
    [childArray, defaultOpenIds],
  )

  const [internalOpenIds, setInternalOpenIds] =
    useState<string[]>(initialOpenIds)

  const isControlled = openIds !== undefined
  const currentOpenIds = isControlled ? openIds : internalOpenIds

  const isExpanded = useCallback(
    (id: string): boolean => currentOpenIds.includes(id),
    [currentOpenIds],
  )

  const toggleItem = useCallback(
    (id: string): void => {
      const isOpen = currentOpenIds.includes(id)
      const nextOpenIds = isOpen
        ? currentOpenIds.filter((itemId) => itemId !== id)
        : allowMultiple
          ? [...currentOpenIds, id]
          : [id]

      if (!isControlled) {
        setInternalOpenIds(nextOpenIds)
      }

      onOpenChange?.(nextOpenIds)
    },
    [allowMultiple, currentOpenIds, isControlled, onOpenChange],
  )

  const contextValue = useMemo(
    () => ({
      grouped,
      allowMultiple,
      darkMode,
      isExpanded,
      toggleItem,
    }),
    [grouped, allowMultiple, darkMode, isExpanded, toggleItem],
  )

  return (
    <AccordionContext.Provider value={contextValue}>
      <div
        className={`ds-accordion${grouped ? ' ds-accordion--grouped' : ''}${
          darkMode ? ' ds-accordion--dark' : ''
        } ${className}`.trim()}
        style={style}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

export default Accordion
